import { useState, useCallback, useEffect, useRef } from 'react'
import {
  getNewsActivitiesByIds,
  getNewsActivitiesPaginated,
  searchNews,
  type PaginatedResult,
} from '@domain/features/news/news-feature'
import type { ActivityExplore } from '@domain/models/activity-explore'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import type { QueryDocumentSnapshot } from 'firebase/firestore'

const PAGE_SIZE = 12
const SEARCH_DEBOUNCE_MS = 350
type NewsFetchPolicy = 'default' | 'server'

function toPublishTimeMs(activity: ActivityExplore): number {
  const timestamp = activity.publishTime?.getTime()
  return typeof timestamp === 'number' && Number.isFinite(timestamp)
    ? timestamp
    : Number.NEGATIVE_INFINITY
}

export function usePaginatedActivities() {
  const ssrNewsList = useSsrPageData()?.newsList
  const [activities, setActivities] = useState<ActivityExplore[]>(
    ssrNewsList?.activities ?? [],
  )
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(!ssrNewsList)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(ssrNewsList?.hasMore ?? true)
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null)
  const [newsCategoryId, setNewsCategoryIdState] = useState<string | null>(null)
  const [searchQuery, setSearchQueryState] = useState('')

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchVersion = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('list')

  const loadPage = useCallback(async (
    reset: boolean,
    catId: string | null,
    cursor: QueryDocumentSnapshot | null,
    fetchPolicy: NewsFetchPolicy = 'default',
  ) => {
    try {
      setLoading(true)
      setError(null)
      const result: PaginatedResult = await getNewsActivitiesPaginated(
        PAGE_SIZE,
        cursor,
        catId,
        fetchPolicy,
      )
      setActivities((prev) => reset ? result.activities : [...prev, ...result.activities])
      setLastDoc(result.lastDoc)
      setHasMore(result.hasMore)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activities')
    } finally {
      setLoading(false)
      setInitialLoading(false)
    }
  }, [])

  const loadInitial = useCallback(async (
    catId: string | null = null,
    fetchPolicy: NewsFetchPolicy = 'default',
  ) => {
    setInitialLoading(true)
    setLastDoc(null)
    await loadPage(true, catId, null, fetchPolicy)
  }, [loadPage])

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return
    await loadPage(false, newsCategoryId, lastDoc)
  }, [loading, hasMore, newsCategoryId, lastDoc, loadPage])

  const performSearch = useCallback(async (
    query: string,
    catId: string | null,
    fetchPolicy: NewsFetchPolicy = 'default',
  ) => {
    const version = ++searchVersion.current

    if (!query.trim()) {
      // Empty query — revert to paginated Firestore results
      setLastDoc(null)
      setInitialLoading(true)
      await loadPage(true, catId, null, fetchPolicy)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const ids = await searchNews(query)

      // Stale check — a newer search may have been triggered
      if (version !== searchVersion.current) return

      if (ids.length === 0) {
        setActivities([])
        setHasMore(false)
        setLoading(false)
        setInitialLoading(false)
        return
      }

      const results = await getNewsActivitiesByIds(ids)

      if (version !== searchVersion.current) return

      const filteredResults = catId
        ? results.filter((activity) => activity.newsCategoryId === catId)
        : results

      const sortedResults = [...filteredResults].sort(
        (left, right) => toPublishTimeMs(right) - toPublishTimeMs(left),
      )

      setActivities(sortedResults)
      setHasMore(false) // No pagination for search results
      setLastDoc(null)
    } catch (err) {
      if (version !== searchVersion.current) return
      setError(err instanceof Error ? err.message : 'Search failed')
    } finally {
      if (version === searchVersion.current) {
        setLoading(false)
        setInitialLoading(false)
      }
    }
  }, [loadPage])

  const refresh = useCallback(async (
    fetchPolicy: NewsFetchPolicy = 'server',
  ) => {
    if (searchQuery.trim()) {
      setLastDoc(null)
      setInitialLoading(true)
      await performSearch(searchQuery, newsCategoryId, fetchPolicy)
      return
    }

    await loadInitial(newsCategoryId, fetchPolicy)
  }, [newsCategoryId, loadInitial, performSearch, searchQuery])

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    refresh: async ({ reason }) => {
      const fetchPolicy: NewsFetchPolicy = reason === 'mount' ? 'default' : 'server'
      await refresh(fetchPolicy)
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  const setCategoryFilter = useCallback(async (newCategoryId: string | null) => {
    setNewsCategoryIdState(newCategoryId)

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    if (searchQuery.trim()) {
      setLastDoc(null)
      setInitialLoading(true)
      await performSearch(searchQuery, newCategoryId)
      return
    }

    await loadInitial(newCategoryId)
  }, [loadInitial, performSearch, searchQuery])

  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query)

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(query, newsCategoryId)
    }, SEARCH_DEBOUNCE_MS)
  }, [performSearch, newsCategoryId])

  const refreshNow = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  useEffect(() => {
    void triggerRefresh('mount', { force: true })
  }, [triggerRefresh])

  return {
    activities,
    loading,
    initialLoading,
    error,
    hasMore,
    loadMore,
    loadInitial,
    newsCategoryId,
    setCategoryFilter,
    searchQuery,
    setSearchQuery,
    refresh: refreshNow,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
